import { useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/use-auth'
import { useSettings } from '@/contexts/setting-context'
import { usersApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogHeader, DialogTitle, DialogClose, DialogContent } from '@/components/ui/dialog'
import { storage } from '@/store/storage'
import {
  User, Sun, Moon, Monitor, Download, Upload, Trash2, Copy, Info, CheckCircle
} from 'lucide-react'

export function SettingsPage() {
  const { user } = useAuth()
  const { settings, updateSettings } = useSettings()
  const [nickname, setNickname] = useState('')
  const [nicknameMsg, setNicknameMsg] = useState('')
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [copied, setCopied] = useState(false)
  const [exporting, setExporting] = useState(false)

  const checkNickname = useCallback(async (val: string) => {
    if (val.length < 2 || val.length > 12) {
      setNicknameMsg('昵称长度需在2-12个字符之间')
      return
    }
    if (val === user?.nickname) {
      setNicknameMsg('')
      return
    }
    try {
      const res = await usersApi.checkNickname(val)
      const data = res as unknown as { data: { available: boolean } }
      setNicknameMsg(data.data.available ? '✓ 昵称可用' : '该昵称已被占用')
    } catch {
      setNicknameMsg('检查失败')
    }
  }, [user?.nickname])

  const saveNickname = async () => {
    if (nickname.length < 2 || nickname.length > 12 || nicknameMsg.includes('占用')) return
    try {
      await usersApi.updateNickname(nickname)
      window.location.reload()
    } catch (e) {
      console.error('Failed to save nickname:', e)
    }
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await usersApi.exportData()
      const data = res as unknown as { data: { records: unknown[] } }
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `life-recorder-backup-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Export failed:', e)
    } finally {
      setExporting(false)
    }
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      try {
        const text = await file.text()
        const data = JSON.parse(text)
        if (data.records && Array.isArray(data.records)) {
          // TODO: implement import via batch API
          console.log('Import records:', data.records.length)
          alert(`导入成功！共 ${data.records.length} 条记录（需要后端支持导入接口）`)
        }
      } catch {
        alert('导入失败：文件格式不正确')
      }
    }
    input.click()
  }

  const handleClearData = async () => {
    try {
      await usersApi.clearData()
      storage.clearOfflineQueue()
      window.location.reload()
    } catch (e) {
      console.error('Clear data failed:', e)
    }
    setShowClearDialog(false)
  }

  const copyDeviceId = () => {
    const id = storage.getDeviceId()
    if (id) {
      navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const themeOptions = [
    { value: 'light' as const, label: '浅色', icon: Sun },
    { value: 'dark' as const, label: '深色', icon: Moon },
    { value: 'system' as const, label: '跟随系统', icon: Monitor },
  ]

  return (
    <div className="flex flex-col min-h-[calc(100dvh-3.5rem)]">
      <div className="px-5 pt-4 pb-3 safe-top">
        <h2 className="text-lg font-bold">设置</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-4">
        {/* Profile */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{user?.nickname || '加载中...'}</p>
                <p className="text-xs text-muted-foreground">点击修改昵称</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="输入新昵称（2-12字符）"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value)
                  if (e.target.value.length >= 2) checkNickname(e.target.value)
                  else setNicknameMsg('')
                }}
                maxLength={12}
              />
              <Button onClick={saveNickname} disabled={nickname.length < 2 || nicknameMsg.includes('占用')}>
                保存
              </Button>
            </div>
            {nicknameMsg && (
              <p className={cn(
                'text-xs mt-2',
                nicknameMsg.includes('可用') ? 'text-green-500' : 'text-destructive'
              )}>
                {nicknameMsg}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Theme */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-3">外观</h3>
            <div className="flex gap-2">
              {themeOptions.map((opt) => {
                const Icon = opt.icon
                const isActive = settings.theme === opt.value
                return (
                  <button
                    key={opt.value}
                    onClick={() => updateSettings({ theme: opt.value })}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all border-2',
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent bg-muted/30 hover:bg-muted/50'
                    )}
                  >
                    <Icon className={cn('h-5 w-5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                    <span className={cn('text-xs font-medium', isActive ? 'text-primary' : 'text-muted-foreground')}>
                      {opt.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Data management */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-3">数据管理</h3>
            <div className="space-y-2">
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-3 w-full py-3 px-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                <Download className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">导出数据</span>
                <span className="text-xs text-muted-foreground ml-auto">JSON</span>
              </button>
              <button
                onClick={handleImport}
                className="flex items-center gap-3 w-full py-3 px-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors text-left"
              >
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">导入数据</span>
                <span className="text-xs text-muted-foreground ml-auto">JSON</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Device info */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-3">设备信息</h3>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-muted/50 rounded-lg px-3 py-2 truncate">
                {storage.getDeviceId()}
              </code>
              <button
                onClick={copyDeviceId}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="border-destructive/30">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-destructive mb-3">危险操作</h3>
            <button
              onClick={() => setShowClearDialog(true)}
              className="flex items-center gap-3 w-full py-3 px-4 rounded-xl bg-destructive/5 hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">清空所有数据</span>
            </button>
          </CardContent>
        </Card>

        {/* About */}
        <div className="text-center py-4 text-xs text-muted-foreground space-y-1">
          <p className="flex items-center justify-center gap-1">
            <Info className="h-3 w-3" />
            极简生活记录器 v1.0.0
          </p>
          <p>数据安全存储在服务器 MySQL 数据库中</p>
        </div>
      </div>

      {/* Clear data confirmation dialog */}
      <Dialog open={showClearDialog} onClose={() => setShowClearDialog(false)}>
        <DialogHeader>
          <DialogTitle>确认清空数据</DialogTitle>
          <DialogClose onClick={() => setShowClearDialog(false)} />
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-muted-foreground mb-4">
            此操作将永久删除您的所有活动记录，且无法恢复。确定要继续吗？
          </p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowClearDialog(false)}>
              取消
            </Button>
            <Button variant="destructive" className="flex-1" onClick={handleClearData}>
              确认清空
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
